import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { downloadAttachment, isSafeApiPath, viewableKind } from "@/lib/files";

// Word bullets use Symbol/Wingdings private-use glyphs browsers can't draw; swap for plain characters.
const GLYPHS = { "\\f0b7": "\\2022", "\\f0a7": "\\25aa", "\\f0d8": "\\27a2", "\\f076": "\\25c6", "\\f0fc": "\\2713", "\\f06f": "\\25e6" };
function fixBullets(root) {
  root.querySelectorAll("style").forEach((s) => {
    let t = s.textContent;
    Object.entries(GLYPHS).forEach(([k, v]) => {
      t = t.split(k).join(v).split(String.fromCharCode(parseInt(k.slice(1), 16))).join(String.fromCharCode(parseInt(v.slice(1), 16)));
    });
    s.textContent = t.replace(/font-family:\s*(Symbol|Wingdings)[^;}]*/gi, "font-family: inherit");
  });
}

const TYPE_LABEL = { pdf: "PDF document", docx: "Word document (DOCX)" };

// Shared new-tab document viewer. Bytes come from the same authorized attachment endpoint; DOCX is
// rendered to HTML in the browser only — the stored original is never converted or replaced.
export default function DocumentPreview() {
  const [sp] = useSearchParams();
  const src = sp.get("src") || "";
  const name = sp.get("name") || "Document";
  const kind = viewableKind(name, "");
  const [state, setState] = useState("loading");
  const [pdfUrl, setPdfUrl] = useState(null);
  const box = useRef(null);

  useEffect(() => { document.title = `${name} — Top-Recruit`; }, [name]);
  useEffect(() => {
    let obj;
    if (!isSafeApiPath(src) || !["pdf", "docx"].includes(kind)) { setState("error"); return undefined; }
    api.get(src, { responseType: "blob" }).then(async (r) => {
      if (kind === "pdf") {
        obj = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        setPdfUrl(obj);
      } else {
        const { renderAsync } = await import("docx-preview");
        await renderAsync(r.data, box.current, undefined, { inWrapper: true, breakPages: true, ignoreLastRenderedPageBreak: false,
          renderHeaders: true, renderFooters: true, useBase64URL: true, experimental: false });
        fixBullets(box.current);
      }
      setState("ready");
    }).catch(() => setState("error"));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [src, kind]);

  const dl = () => downloadAttachment(src, name).catch(() => setState("error"));
  const close = () => { window.close(); if (!window.closed) window.history.back(); };

  return (
    <div className="h-screen flex flex-col bg-slate-100" data-testid="document-preview-page">
      <header className="flex items-center gap-3 px-4 sm:px-6 py-3 bg-white border-b border-slate-200 shadow-sm">
        <FileText className="w-5 h-5 text-brand shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate" data-testid="document-preview-name">{name}</p>
          <p className="text-xs text-slate-500" data-testid="document-preview-type">{TYPE_LABEL[kind] || "Document"}</p>
        </div>
        <Button size="sm" onClick={dl} className="bg-brand hover:bg-brand-strong" data-testid="document-preview-download"><Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Download Original</span></Button>
        <Button size="sm" variant="outline" onClick={close} data-testid="document-preview-close"><X className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Close</span></Button>
      </header>
      <main className="flex-1 min-h-0 overflow-auto relative">
        {state === "loading" && <div className="absolute inset-0 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {state === "error" && (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-slate-600 px-6 text-center" data-testid="document-preview-unavailable">
            <FileText className="w-10 h-10 text-slate-300" />
            <p className="font-medium">Preview unavailable for this document.</p>
            {isSafeApiPath(src) && <Button variant="outline" onClick={() => downloadAttachment(src, name)} data-testid="document-preview-download-fallback"><Download className="w-4 h-4 mr-1.5" />Download Original</Button>}
          </div>
        )}
        {kind === "pdf" && pdfUrl && <iframe title={name} src={pdfUrl} className="w-full h-full border-0 bg-white" data-testid="document-preview-pdf" />}
        {kind === "docx" && <div ref={box} className={`docx-host ${state === "error" ? "hidden" : ""}`} data-testid="document-preview-docx" />}
      </main>
    </div>
  );
}
